import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogOut, Plus, Trash2, Download, Save, ArrowLeft, Power, Upload, Image as ImageIcon, Crown, Calculator, EyeOff, RotateCcw } from "lucide-react";
import { api, API, getAdminToken, setAdminToken, clearAdminToken } from "../lib/api";
import { ASSETS } from "../lib/constants";

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [password, setPassword] = useState("");
  const [securityCode, setSecurityCode] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await api.post("/admin/login", { password, security_code: securityCode });
      setAdminToken(data.token);
      setAuthed(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login fehlgeschlagen.");
    } finally {
      setLoggingIn(false);
    }
  };

  if (!authed) {
    return (
      <div className="grain min-h-screen flex items-center justify-center px-5">
        <div className="glass rounded-3xl p-8 sm:p-10 w-full max-w-md" data-testid="admin-login">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-[#9fb2cc] hover:text-[#e9b44c] mb-8">
            <ArrowLeft size={14} /> Zur Landingpage
          </Link>
          <div className="flex items-center gap-3 mb-7">
            <img src={ASSETS.logo} alt="TSG" className="h-14 w-auto object-contain" />
            <div>
              <h1 className="font-head text-2xl uppercase leading-none">Adminbereich</h1>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#9fb2cc] mt-1">TSG Sprockhövel</p>
            </div>
          </div>
          <form onSubmit={login}>
            <label className="text-xs tracking-[0.15em] uppercase text-[#9fb2cc] font-semibold flex items-center gap-2 mb-2">
              <Lock size={13} /> Passwort
            </label>
            <input type="password" className="field" placeholder="••••••••" value={password}
              onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" autoFocus />
            <label className="text-xs tracking-[0.15em] uppercase text-[#9fb2cc] font-semibold flex items-center gap-2 mb-2 mt-5">
              <Lock size={13} /> Sicherheitszahl
            </label>
            <input type="password" inputMode="numeric" className="field" placeholder="••••" value={securityCode}
              onChange={(e) => setSecurityCode(e.target.value)} data-testid="admin-security-code" />
            <button type="submit" disabled={loggingIn} className="btn-primary w-full py-4 mt-6 text-sm" data-testid="admin-login-btn">
              {loggingIn ? "…" : "Anmelden"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return <Dashboard onLogout={() => { clearAdminToken(); setAuthed(false); }} />;
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("spiele");
  const [config, setConfig] = useState(null);
  const [matches, setMatches] = useState([]);
  const [subs, setSubs] = useState([]);

  const loadAll = async () => {
    try {
      const [pub, s] = await Promise.all([api.get("/public/state"), api.get("/admin/submissions")]);
      setConfig(pub.data.config);
      setMatches(pub.data.matches || []);
      setSubs(s.data || []);
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Sitzung abgelaufen."); onLogout(); }
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const toggleGame = async (action) => {
    try {
      const { data } = await api.post(`/admin/game/${action}`);
      setConfig(data);
      toast.success(action === "open" ? "Tippspiel geöffnet." : "Tippspiel geschlossen.");
    } catch { toast.error("Aktion fehlgeschlagen."); }
  };

  const downloadCsv = () => window.open(`${API}/admin/submissions.csv?token=${getAdminToken()}`, "_blank");

  return (
    <div className="grain min-h-screen">
      <header className="border-b border-white/10 px-5 sm:px-8 py-4 flex items-center justify-between sticky top-0 z-20 bg-[#01122f]/90 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <img src={ASSETS.logo} alt="TSG" className="h-11 w-auto object-contain" />
          <h1 className="font-head text-xl uppercase">Admin · Tippspiel</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-3 py-1.5 rounded-full border ${config?.game_open ? "border-[#10B981]/50 text-[#10B981] bg-[#10B981]/10" : "border-white/20 text-[#9fb2cc]"}`} data-testid="game-status">
            {config?.game_open ? "Geöffnet" : "Geschlossen"}
          </span>
          <button onClick={() => toggleGame(config?.game_open ? "close" : "open")} className="btn-ghost text-xs px-4 py-2 flex items-center gap-2" data-testid="toggle-game-btn">
            <Power size={13} /> {config?.game_open ? "Schließen" : "Öffnen"}
          </button>
          <button onClick={onLogout} className="btn-ghost text-xs px-4 py-2 flex items-center gap-2" data-testid="logout-btn">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </header>

      <div className="px-5 sm:px-8 pt-6 flex gap-2 max-w-6xl mx-auto">
        {[["spiele", "Spiele"], ["auswertung", "Auswertung"], ["einstellungen", "Einstellungen"], ["tipps", `Tipps (${subs.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`font-head uppercase tracking-wide text-sm px-5 py-2.5 rounded-full transition-colors ${tab === k ? "bg-[#e9b44c] text-[#041019]" : "text-[#9fb2cc] hover:text-white"}`}
            data-testid={`tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      <main className="px-5 sm:px-8 py-8 max-w-6xl mx-auto">
        {tab === "spiele" && <MatchesTab matches={matches} reload={loadAll} />}
        {tab === "auswertung" && <AuswertungTab config={config} reload={loadAll} />}
        {tab === "einstellungen" && <SettingsTab config={config} onSaved={setConfig} />}
        {tab === "tipps" && <TipsTab subs={subs} matches={matches} onDownload={downloadCsv} />}
      </main>
    </div>
  );
}

const emptyMatch = () => ({ home: "", away: "", kickoff: "", trainer_home: "", trainer_away: "", sort_order: 99 });

function MatchRow({ m, reload }) {
  const [row, setRow] = useState({
    ...m,
    trainer_home: m.trainer_home ?? "",
    trainer_away: m.trainer_away ?? "",
    final_home: m.final_home ?? "",
    final_away: m.final_away ?? "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        home: row.home, away: row.away, kickoff: row.kickoff, sort_order: Number(row.sort_order) || 0,
        trainer_home: row.trainer_home === "" ? null : Number(row.trainer_home),
        trainer_away: row.trainer_away === "" ? null : Number(row.trainer_away),
        final_home: row.final_home === "" ? null : Number(row.final_home),
        final_away: row.final_away === "" ? null : Number(row.final_away),
      };
      if (m.id) await api.put(`/admin/matches/${m.id}`, body);
      else await api.post("/admin/matches", body);
      toast.success("Gespeichert.");
      reload();
    } catch { toast.error("Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!m.id) return reload();
    try { await api.delete(`/admin/matches/${m.id}`); toast.success("Gelöscht."); reload(); }
    catch { toast.error("Löschen fehlgeschlagen."); }
  };

  return (
    <div className="glass rounded-2xl p-4 grid gap-3 md:grid-cols-[2fr_2fr_1.6fr_auto_auto_auto] md:items-end" data-testid="admin-match-row">
      <Field label="Heim"><input className="field !h-11" value={row.home} onChange={(e) => setRow({ ...row, home: e.target.value })} /></Field>
      <Field label="Gast"><input className="field !h-11" value={row.away} onChange={(e) => setRow({ ...row, away: e.target.value })} /></Field>
      <Field label="Anstoß"><input className="field !h-11" value={row.kickoff} onChange={(e) => setRow({ ...row, kickoff: e.target.value })} placeholder="So., 23.08.2026 · 15:00" /></Field>
      <Field label="Tipp Protagonist">
        <div className="flex items-center gap-1">
          <input className="field !h-11 !w-14 text-center !px-1" type="number" value={row.trainer_home} onChange={(e) => setRow({ ...row, trainer_home: e.target.value })} />
          <span className="text-[#e9b44c]">:</span>
          <input className="field !h-11 !w-14 text-center !px-1" type="number" value={row.trainer_away} onChange={(e) => setRow({ ...row, trainer_away: e.target.value })} />
        </div>
      </Field>
      <Field label="Endstand">
        <div className="flex items-center gap-1">
          <input className="field !h-11 !w-14 text-center !px-1 !border-[#10B981]/40" type="number" value={row.final_home} onChange={(e) => setRow({ ...row, final_home: e.target.value })} data-testid="final-home" />
          <span className="text-[#10B981]">:</span>
          <input className="field !h-11 !w-14 text-center !px-1 !border-[#10B981]/40" type="number" value={row.final_away} onChange={(e) => setRow({ ...row, final_away: e.target.value })} data-testid="final-away" />
        </div>
      </Field>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="btn-primary !rounded-xl px-3 h-11 flex items-center" title="Speichern" data-testid="save-match-btn"><Save size={16} /></button>
        <button onClick={del} className="btn-ghost !rounded-xl px-3 h-11 flex items-center text-[#EF4444]" title="Löschen" data-testid="delete-match-btn"><Trash2 size={16} /></button>
      </div>
    </div>
  );
}

function MatchesTab({ matches, reload }) {
  const [adding, setAdding] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [text, setText] = useState("");
  const [importing, setImporting] = useState(false);

  const doImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/admin/matches/bulk", { text, replace: true });
      toast.success(`${data.imported} Spiele importiert.`);
      setText(""); setShowImport(false); reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fehlgeschlagen.");
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h2 className="font-head text-2xl uppercase">Spiele des Spieltags</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowImport((s) => !s)} className="btn-ghost text-xs px-4 py-2.5 flex items-center gap-2" data-testid="toggle-import-btn"><Upload size={14} /> Spiele importieren</button>
          <button onClick={() => setAdding(true)} className="btn-primary text-xs px-4 py-2.5 flex items-center gap-2" data-testid="add-match-btn"><Plus size={14} /> Spiel hinzufügen</button>
        </div>
      </div>

      {showImport && (
        <div className="glass rounded-2xl p-5" data-testid="import-box">
          <p className="text-sm text-[#cbd5e1] mb-2">Füge die Paarungen ein – <strong>eine pro Zeile</strong>, im Format <span className="text-[#e9b44c]">Heim - Gast</span>. Ersetzt alle bisherigen Spiele.</p>
          <textarea
            value={text} onChange={(e) => setText(e.target.value)} rows={9}
            className="field !h-auto py-3 w-full font-mono text-sm leading-relaxed"
            placeholder={"TSG Sprockhövel - TuS Hiltrup\n1. FC Gievenbeck - SpVgg Erkenschwick\nSV Schermbeck - DSC Arminia Bielefeld II"}
            data-testid="import-textarea"
          />
          <div className="flex gap-2 mt-3">
            <button onClick={doImport} disabled={importing || !text.trim()} className="btn-primary text-sm px-5 py-2.5 flex items-center gap-2" data-testid="run-import-btn"><Upload size={15} /> {importing ? "Importiere…" : "Importieren"}</button>
            <button onClick={() => setShowImport(false)} className="btn-ghost text-sm px-5 py-2.5">Abbrechen</button>
          </div>
        </div>
      )}

      {adding && <MatchRow m={emptyMatch()} reload={() => { setAdding(false); reload(); }} />}
      {matches.map((m) => <MatchRow key={m.id} m={m} reload={reload} />)}
      {matches.length === 0 && !adding && <p className="text-[#9fb2cc] text-sm">Noch keine Spiele. Füge das erste Spiel hinzu oder importiere die Paarungen.</p>}
    </div>
  );
}

function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function AuswertungTab({ config, reload }) {
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => { api.get("/public/ranking").then(({ data }) => setRanking(data)).catch(() => {}); }, []);

  const compute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/compute-ranking");
      setRanking({ published: true, ranking: data.ranking, computed_at: data.computed_at });
      toast.success("Punkte berechnet & Wochenranking veröffentlicht.");
    } catch { toast.error("Berechnung fehlgeschlagen."); }
    finally { setLoading(false); }
  };

  const hide = async () => {
    try { await api.post("/admin/ranking/hide"); setRanking((r) => ({ ...r, published: false })); toast.success("Wochenranking ausgeblendet."); }
    catch { toast.error("Fehlgeschlagen."); }
  };

  const resetMatchday = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/new-matchday");
      setRanking({ published: false, ranking: [] });
      setConfirming(false);
      toast.success(`Neuer Spieltag gestartet. ${data.deleted_submissions} Abgabe(n) gelöscht.`);
      if (reload) await reload();
    } catch { toast.error("Zurücksetzen fehlgeschlagen."); }
    finally { setResetting(false); }
  };

  return (
    <div className="space-y-6" data-testid="auswertung-tab">
      <div className="glass rounded-2xl p-6 sm:p-8">
        <h2 className="font-head text-2xl uppercase mb-2">Auswertung & Wochenranking</h2>
        <p className="text-sm text-[#9fb2cc] mb-5 max-w-2xl">
          Trage zuerst im Tab <strong className="text-white">Spiele</strong> die Endstände ein (Feld „Endstand") und speichere jedes Spiel.
          Klicke dann hier, um die Punkte automatisch zu berechnen und das Wochenranking zu veröffentlichen.
          Punkte: 3 = exakter Tipp, 2,5 = richtige Tordifferenz, 2 = richtige Tendenz.
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={compute} disabled={loading} className="btn-primary px-6 py-3 text-sm flex items-center gap-2" data-testid="compute-ranking-btn">
            <Calculator size={16} /> {loading ? "Berechne…" : "Punkte berechnen & veröffentlichen"}
          </button>
          {ranking?.published && (
            <button onClick={hide} className="btn-ghost px-5 py-3 text-sm flex items-center gap-2" data-testid="hide-ranking-btn">
              <EyeOff size={15} /> Übersicht ausblenden
            </button>
          )}
        </div>
        {ranking?.published && (
          <p className="text-xs text-[#10B981] mt-3">● Wochenranking ist öffentlich sichtbar.</p>
        )}
      </div>

      {ranking?.ranking?.length > 0 && (
        <div className="glass rounded-2xl overflow-hidden" data-testid="admin-ranking">
          <div className="px-5 py-4 border-b border-white/10 font-head uppercase text-lg">Aktuelles Ranking</div>
          {ranking.ranking.map((r, i) => (
            <div key={i} className={`px-5 py-3 flex items-center gap-4 border-b border-white/5 ${r.protagonist ? "bg-[#e9b44c]/10" : ""}`}>
              <span className="font-head text-xl w-8 text-white/50">{r.rank || i + 1}</span>
              <span className="flex-1 font-head text-lg uppercase flex items-center gap-2 min-w-0">
                <span className="truncate">{r.name}</span>
                {r.protagonist && <Crown size={14} className="text-[#e9b44c] shrink-0" />}
              </span>
              <span className="font-head text-2xl text-[#e9b44c] tabular-nums">{String(r.points).replace(".", ",")}</span>
              <span className="text-[10px] uppercase tracking-widest text-[#9fb2cc]">Pkt</span>
            </div>
          ))}
        </div>
      )}

      <div className="glass rounded-2xl p-6 sm:p-8 border border-[#EF4444]/30" data-testid="new-matchday-card">
        <h2 className="font-head text-2xl uppercase mb-2 flex items-center gap-2"><RotateCcw size={20} className="text-[#EF4444]" /> Neuen Spieltag starten</h2>
        <p className="text-sm text-[#9fb2cc] mb-5 max-w-2xl">
          Löscht <strong className="text-white">alle Abgaben</strong> und <strong className="text-white">Endergebnisse</strong>, blendet das Ranking aus und öffnet das Tippspiel wieder.
          Die Spiele-Liste und der Spieltagspate bleiben erhalten (im Tab <strong className="text-white">Spiele</strong> anpassen bzw. neue Paarungen importieren).
          <br />Diese Aktion kann <strong className="text-white">nicht rückgängig</strong> gemacht werden.
        </p>
        {!confirming ? (
          <button onClick={() => setConfirming(true)} className="btn-ghost px-6 py-3 text-sm flex items-center gap-2 text-[#EF4444] border-[#EF4444]/40" data-testid="new-matchday-btn">
            <RotateCcw size={16} /> Neuen Spieltag starten
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-white font-semibold">Wirklich alles zurücksetzen?</span>
            <button onClick={resetMatchday} disabled={resetting} className="px-6 py-3 text-sm font-semibold rounded-full bg-[#EF4444] text-white hover:bg-[#dc2626] transition-colors flex items-center gap-2" data-testid="confirm-new-matchday-btn">
              <RotateCcw size={16} /> {resetting ? "Setze zurück…" : "Ja, zurücksetzen"}
            </button>
            <button onClick={() => setConfirming(false)} disabled={resetting} className="btn-ghost px-5 py-3 text-sm" data-testid="cancel-new-matchday-btn">
              Abbrechen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsTab({ config, onSaved }) {
  const [c, setC] = useState({ matchday_label: "", opponent_name: "", opponent_position: "", prize: "", deadlineLocal: "", bonus_participants: 0 });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [prizeUploading, setPrizeUploading] = useState(false);
  const prizeFileRef = useRef(null);
  useEffect(() => {
    if (config) setC({
      matchday_label: config.matchday_label || "",
      opponent_name: config.opponent_name || "",
      opponent_position: config.opponent_position || "",
      opponent_bio: config.opponent_bio || "",
      prize: config.prize || "",
      whatsapp_url: config.whatsapp_url || "",
      instagram_url: config.instagram_url || "",
      bonus_participants: config.bonus_participants ?? 0,
      deadlineLocal: isoToLocalInput(config.deadline),
    });
  }, [config]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        matchday_label: c.matchday_label,
        opponent_name: c.opponent_name,
        opponent_position: c.opponent_position,
        opponent_bio: c.opponent_bio,
        prize: c.prize,
        whatsapp_url: c.whatsapp_url,
        instagram_url: c.instagram_url,
        bonus_participants: Math.max(0, parseInt(c.bonus_participants, 10) || 0),
        deadline: c.deadlineLocal ? new Date(c.deadlineLocal).toISOString() : null,
      };
      const { data } = await api.put("/admin/config", body);
      onSaved(data);
      toast.success("Einstellungen gespeichert.");
    } catch { toast.error("Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/opponent-image", fd);
      onSaved({ ...config, opponent_image: data.opponent_image });
      toast.success("Bild hochgeladen.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = async () => {
    try {
      await api.delete("/admin/opponent-image");
      onSaved({ ...config, opponent_image: null });
      toast.success("Bild entfernt.");
    } catch { toast.error("Entfernen fehlgeschlagen."); }
  };

  const uploadPrizeImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPrizeUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/prize-image", fd);
      onSaved({ ...config, prize_image: data.prize_image });
      toast.success("Gewinn-Bild hochgeladen.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen.");
    } finally {
      setPrizeUploading(false);
      if (prizeFileRef.current) prizeFileRef.current.value = "";
    }
  };

  const removePrizeImage = async () => {
    try {
      await api.delete("/admin/prize-image");
      onSaved({ ...config, prize_image: null });
      toast.success("Gewinn-Bild entfernt.");
    } catch { toast.error("Entfernen fehlgeschlagen."); }
  };

  return (
    <div className="glass rounded-2xl p-6 sm:p-8 max-w-2xl space-y-5" data-testid="settings-tab">
      <h2 className="font-head text-2xl uppercase">Gegner & Einstellungen</h2>

      <Field label="Gegner-Foto">
        <div className="flex items-center gap-4">
          <div className="h-24 w-24 rounded-2xl overflow-hidden bg-white/5 border border-white/10 shrink-0 flex items-center justify-center">
            {config?.opponent_image
              ? <img src={config.opponent_image} alt="Gegner" className="h-full w-full object-cover object-top" data-testid="opponent-image-preview" />
              : <ImageIcon size={26} className="text-[#9fb2cc]" />}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadImage} className="hidden" data-testid="opponent-image-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-primary text-xs px-4 py-2.5 flex items-center gap-2 w-max" data-testid="upload-image-btn">
              <Upload size={14} /> {uploading ? "Lädt hoch…" : "Bild hochladen"}
            </button>
            {config?.opponent_image && (
              <button type="button" onClick={removeImage} className="btn-ghost text-xs px-4 py-2 flex items-center gap-2 w-max text-[#EF4444]" data-testid="remove-image-btn">
                <Trash2 size={13} /> Entfernen
              </button>
            )}
            <p className="text-[11px] text-[#9fb2cc]">JPG, PNG oder WebP · max. 6 MB. Hochformat wirkt am besten.</p>
          </div>
        </div>
      </Field>

      <Field label="Name des Gegners"><input className="field" value={c.opponent_name} onChange={(e) => setC({ ...c, opponent_name: e.target.value })} data-testid="cfg-opponent-name" placeholder="z. B. Andrius Balaika" /></Field>
      <Field label="Position / Rolle"><input className="field" value={c.opponent_position} onChange={(e) => setC({ ...c, opponent_position: e.target.value })} data-testid="cfg-opponent-position" placeholder="z. B. Trainer · TSG Sprockhövel" /></Field>
      <Field label="Zur Person (Text über den Spieltagspaten)">
        <textarea className="field min-h-[120px] resize-y" value={c.opponent_bio} onChange={(e) => setC({ ...c, opponent_bio: e.target.value })} data-testid="cfg-opponent-bio" placeholder="Schreibe hier etwas über den Spieltagspaten – z. B. Werdegang, Erfolge, Lieblingsverein…" />
        <p className="text-[11px] text-[#9fb2cc] mt-1.5">Wird auf der Startseite sauber unter dem Bild des Spieltagspaten angezeigt.</p>
      </Field>
      <Field label="Spieltag – nur die Bezeichnung (Oberliga Westfalen steht fest darüber)"><input className="field" value={c.matchday_label} onChange={(e) => setC({ ...c, matchday_label: e.target.value })} data-testid="cfg-matchday" placeholder="z. B. 2. Spieltag" /></Field>
      <Field label="Gewinn"><input className="field" value={c.prize} onChange={(e) => setC({ ...c, prize: e.target.value })} data-testid="cfg-prize" /></Field>
      <Field label="Gewinn-Bild (optional)">
        <div className="flex items-center gap-4">
          <div className="h-24 w-24 rounded-2xl overflow-hidden bg-white/5 border border-white/10 shrink-0 flex items-center justify-center">
            {config?.prize_image
              ? <img src={config.prize_image} alt="Gewinn" className="h-full w-full object-cover" data-testid="prize-image-preview" />
              : <ImageIcon size={26} className="text-[#9fb2cc]" />}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={prizeFileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadPrizeImage} className="hidden" data-testid="prize-image-input" />
            <button type="button" onClick={() => prizeFileRef.current?.click()} disabled={prizeUploading} className="btn-primary text-xs px-4 py-2.5 flex items-center gap-2 w-max" data-testid="upload-prize-image-btn">
              <Upload size={14} /> {prizeUploading ? "Lädt hoch…" : "Bild hochladen"}
            </button>
            {config?.prize_image && (
              <button type="button" onClick={removePrizeImage} className="btn-ghost text-xs px-4 py-2 flex items-center gap-2 w-max text-[#EF4444]" data-testid="remove-prize-image-btn">
                <Trash2 size={13} /> Entfernen
              </button>
            )}
            <p className="text-[11px] text-[#9fb2cc]">JPG, PNG oder WebP · max. 6 MB. Empfohlen: Querformat ca. 1000×750 px (4:3) oder quadratisch 800×800 px.</p>
          </div>
        </div>
      </Field>
      <Field label="Annahmeschluss (Freitag vor dem Spieltag)">
        <input type="datetime-local" className="field" value={c.deadlineLocal} onChange={(e) => setC({ ...c, deadlineLocal: e.target.value })} data-testid="cfg-deadline" />
        <p className="text-[11px] text-[#9fb2cc] mt-1.5">Nach diesem Zeitpunkt sind keine Tipps mehr möglich. Leer lassen = kein automatischer Schluss.</p>
      </Field>
      <Field label="WhatsApp-Kanal-Link"><input className="field" value={c.whatsapp_url} onChange={(e) => setC({ ...c, whatsapp_url: e.target.value })} data-testid="cfg-whatsapp" placeholder="https://whatsapp.com/channel/..." /></Field>
      <Field label="Instagram-Link"><input className="field" value={c.instagram_url} onChange={(e) => setC({ ...c, instagram_url: e.target.value })} data-testid="cfg-instagram" placeholder="https://instagram.com/tsg_sprockhoevel" /></Field>
      <Field label="Teilnehmer-Bonus (wird auf den echten Zähler addiert)">
        <input type="number" min="0" className="field" value={c.bonus_participants} onChange={(e) => setC({ ...c, bonus_participants: e.target.value })} data-testid="cfg-bonus-participants" />
        <p className="text-[11px] text-[#9fb2cc] mt-1.5">Auf der Startseite steht: „Diese Woche tippen bereits X Teilnehmer" = echte Abgaben + dieser Bonus. 0 = nur echte Zahl.</p>
      </Field>
      <button onClick={save} disabled={saving} className="btn-primary px-6 py-3 text-sm flex items-center gap-2" data-testid="save-config-btn"><Save size={15} /> Speichern</button>
    </div>
  );
}

function TipsTab({ subs, matches, onDownload }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-head text-2xl uppercase">Abgegebene Tipps</h2>
        <button onClick={onDownload} className="btn-primary text-xs px-4 py-2.5 flex items-center gap-2" data-testid="csv-download-btn"><Download size={14} /> CSV herunterladen</button>
      </div>
      {subs.length === 0 ? (
        <p className="text-[#9fb2cc] text-sm">Noch keine Tipps abgegeben.</p>
      ) : (
        <div className="glass rounded-2xl overflow-x-auto no-scrollbar" data-testid="tips-table">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-[#9fb2cc] border-b border-white/10">
                <th className="p-4">Rankingname</th>
                <th className="p-4">Instagram</th>
                <th className="p-4">E-Mail</th>
                {matches.map((m) => <th key={m.id} className="p-3 text-center whitespace-nowrap">{m.home.slice(0, 10)}<br />{m.away.slice(0, 10)}</th>)}
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => {
                const tipMap = {}; (s.tips || []).forEach((t) => { tipMap[`${t.home}|${t.away}`] = `${t.tip_home}:${t.tip_away}`; });
                return (
                  <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="p-4 whitespace-nowrap font-medium">{s.first_name || "–"}</td>
                    <td className="p-4 whitespace-nowrap text-[#9fb2cc]">{s.instagram_name || "–"}</td>
                    <td className="p-4 whitespace-nowrap text-[#9fb2cc]">{s.email}</td>
                    {matches.map((m) => <td key={m.id} className="p-3 text-center font-head text-[#e9b44c]">{tipMap[`${m.home}|${m.away}`] || "–"}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.15em] uppercase text-[#9fb2cc] font-semibold block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
